import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Barcode from "react-barcode";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiError } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { Printer, PrinterCheck, FileDown, Barcode as BarcodeIcon, Camera } from "lucide-react";
import { toast } from "sonner";
import BarcodeCameraScanner from "../components/BarcodeCameraScanner";

// Physical label size — 95mm × 95mm thermal label (with tolerance)
export default function LabelsPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const orderIds = params.get("order_ids");
  const autoPrint = params.get("autoprint") === "1";
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const ids = orderIds ? orderIds.split(",").filter(Boolean) : (orderId ? [orderId] : []);
    if (ids.length === 0) return;
    (async () => {
      try {
        const results = await Promise.all(ids.map(id => api.get(`/orders/${id}`).then(r => r.data)));
        setOrders(results);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [orderId, orderIds]);

  useEffect(() => {
    if (autoPrint && orders.length > 0) {
      const t = setTimeout(() => window.print(), 700);
      return () => clearTimeout(t);
    }
  }, [autoPrint, orders]);

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    try {
      const ids = orderIds ? orderIds : orderId;
      const key = orderIds ? "order_ids" : "order_id";
      const res = await api.get(`/exports/labels-pdf`, { params: { [key]: ids }, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Open the PDF in a new tab — the browser's PDF viewer allows printing with correct 100x100mm size
      const w = window.open(url, "_blank");
      if (w) {
        // Auto-trigger print dialog once loaded
        w.addEventListener?.("load", () => setTimeout(() => w.print?.(), 500));
      }
      toast.success("PDF hazır — yazdırma diyaloğu açılıyor");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAutoPrint = () => {
    const url = window.location.pathname + window.location.search + (window.location.search.includes("?") ? "&" : "?") + "autoprint=1";
    window.open(url, "_blank");
  };

  if (!orderId && !orderIds) {
    return <SingleLabelReprint />;
  }
  if (orders.length === 0) return <div className="text-slate-500">Yükleniyor...</div>;

  const allPieces = orders.flatMap(o => o.pieces.map(p => ({ ...p, _order: o })));
  const totalLabels = allPieces.length;
  const subtitle = orders.length === 1
    ? `${orders[0].order_number} — ${orders[0].customer_name}`
    : `${orders.length} sipariş — ${totalLabels} etiket`;

  return (
    <div>
      <div className="print:hidden">
        <PageHeader
          title="Etiket Önizleme"
          subtitle={subtitle}
          actions={
            <div className="flex gap-2 flex-wrap">
              <button onClick={handleDownloadPDF} data-testid="pdf-labels-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wide">
                <FileDown size={18} /> PDF Aç & Yazdır
              </button>
              <button onClick={handleAutoPrint} data-testid="autoprint-labels-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-white border-2 border-slate-800 text-slate-900 font-bold uppercase tracking-wide text-sm hover:bg-slate-100">
                <PrinterCheck size={18} /> HTML Yazdır
              </button>
            </div>
          }
        />
        <div className="mb-4 p-3 border-2 border-emerald-500 bg-emerald-50 text-emerald-900 text-sm">
          <b>✓ Tavsiye edilen: "PDF Aç & Yazdır"</b> — Etiket boyutu (95 × 95 mm, güvenli tolerans dahil) PDF içine gömülü olarak gelir. Tarayıcı ayarlarıyla uğraşmadan doğrudan yazıcıya gönderir.
          <div className="mt-1 text-xs">
            Yazdırma diyaloğunda <b>"Ölçek: Gerçek boyut" (Actual size)</b> veya <b>"%100"</b> seçili olduğundan emin olun.
          </div>
        </div>
      </div>

      <div className="print-labels-container flex flex-wrap gap-4">
        {allPieces.map((p) => (
          <LabelCard key={p.id} piece={p} />
        ))}
      </div>

      <style>{`
        .label-card {
          width: 95mm;
          height: 95mm;
          box-sizing: border-box;
          background: white;
          border: 2px solid #0f172a;
          padding: 3mm;
          display: grid;
          grid-template-rows: 6mm 24mm 1fr 13mm 20mm;
          gap: 1.5mm;
          font-family: 'IBM Plex Sans', system-ui, sans-serif;
          color: #0f172a;
          overflow: hidden;
        }
        .lbl-brand {
          background: #0f172a; color: white; padding: 1mm 2mm;
          font-weight: 900; font-size: 9pt; letter-spacing: 0.05em;
          width: fit-content; max-width: 55mm;
        }
        .lbl-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 2mm; }
        .lbl-header-left { flex: 1; min-width: 0; }
        .lbl-header-right { padding-top: 2mm; padding-right: 2mm; }
        .lbl-key { font-size: 8pt; color: #64748b; letter-spacing: 0.03em; text-transform: uppercase; font-weight: 700; line-height: 1.1; }
        .lbl-customer { font-size: 15pt; font-weight: 900; line-height: 1.05; margin-top: 0.5mm; word-break: break-word; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
        .lbl-order { font-size: 13pt; font-weight: 800; font-family: 'IBM Plex Mono', ui-monospace, monospace; margin-top: 1mm; }

        .lbl-info { display: grid; grid-template-columns: 1fr 1fr; gap: 2mm 3mm; align-content: center; }
        .lbl-info-val { font-size: 12pt; font-weight: 800; line-height: 1.1; word-break: break-word; }
        .lbl-info-val.mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; }

        .lbl-piece {
          display: flex; align-items: center; justify-content: space-between;
          background: #1d4ed8; color: white; padding: 0 3mm;
        }
        .lbl-piece .k { font-size: 10pt; letter-spacing: 0.08em; font-weight: 800; }
        .lbl-piece .v { font-size: 22pt; font-weight: 900; font-family: 'IBM Plex Mono', ui-monospace, monospace; line-height: 1; }

        .lbl-barcode-wrap { display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .lbl-barcode-wrap svg { max-width: 89mm; height: auto; }

        @media print {
          @page {
            size: 95mm 95mm;
            margin: 0;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          aside, header, .print\\:hidden, [data-testid="print-tip"] { display: none !important; }
          main { padding: 0 !important; margin: 0 !important; }
          .print-labels-container {
            display: block !important;
            gap: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .label-card {
            border: none !important;
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin: 0 !important;
            box-shadow: none !important;
          }
          .label-card:last-child { page-break-after: auto !important; break-after: auto !important; }
        }
      `}</style>
    </div>
  );
}

function LabelCard({ piece }) {
  const o = piece._order;
  return (
    <div className="label-card" data-testid={`label-${piece.barcode}`}>
      {/* Brand strip (top-left) */}
      <div className="lbl-brand">MARSELLA BEDDING</div>

      {/* HEADER: Customer + Order + QR (moved down/inward) */}
      <div className="lbl-header">
        <div className="lbl-header-left">
          <div className="lbl-key">Müşteri</div>
          <div className="lbl-customer">{o.customer_name}</div>
          <div className="lbl-order">{o.order_number}</div>
        </div>
        <div className="lbl-header-right">
          <QRCodeCanvas value={piece.barcode} size={78} includeMargin={false} />
        </div>
      </div>

      {/* BODY: Product info */}
      <div className="lbl-info">
        <div>
          <div className="lbl-key">Ürün</div>
          <div className="lbl-info-val">{piece.category} {piece.model}</div>
        </div>
        <div>
          <div className="lbl-key">Ölçü</div>
          <div className="lbl-info-val mono">{piece.size}</div>
        </div>
        <div>
          <div className="lbl-key">Kumaş</div>
          <div className="lbl-info-val">{piece.fabric || "—"}</div>
        </div>
        <div>
          <div className="lbl-key">Ürün</div>
          <div className="lbl-info-val mono">{piece.product_number} / {piece.product_total}</div>
        </div>
      </div>

      {/* PIECE indicator */}
      <div className="lbl-piece">
        <span className="k">PARÇA</span>
        <span className="v">{piece.piece_number} / {piece.piece_total}</span>
      </div>

      {/* Code128 barcode (thinner) */}
      <div className="lbl-barcode-wrap">
        <Barcode
          value={piece.barcode}
          format="CODE128"
          width={1.2}
          height={40}
          fontSize={11}
          margin={0}
          displayValue={true}
        />
      </div>
    </div>
  );
}

function SingleLabelReprint() {
  const [barcode, setBarcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {ok, msg}

  const reprint = async (bc) => {
    const code = (bc || barcode).trim();
    if (!code) { toast.error("Barkod girin"); return; }
    setLoading(true);
    setLastResult(null);
    try {
      const res = await api.get("/exports/labels-pdf", {
        params: { barcode: code },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // PDF'yi yeni sekmede aç, yazdırma diyaloğunu tetikle
      const w = window.open(url, "_blank");
      if (w) w.addEventListener?.("load", () => setTimeout(() => w.print?.(), 500));
      // Bir de indirilebilir tut
      const a = document.createElement("a");
      a.href = url;
      a.download = `etiket_${code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setLastResult({ ok: true, msg: `Etiket hazır: ${code}` });
      toast.success(`Etiket hazır: ${code}`);
      setBarcode("");
    } catch (e) {
      const msg = formatApiError(e);
      setLastResult({ ok: false, msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => { e.preventDefault(); reprint(); };

  return (
    <div>
      <PageHeader
        title="Etiket Yeniden Bastır"
        subtitle="Bir barkod kaybolduysa buradan tekil etiketi tek tıkla PDF olarak indirin."
      />

      <div className="card-industrial p-5 max-w-2xl">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-600 mb-3 inline-flex items-center gap-2">
          <BarcodeIcon size={14}/> Barkod Girin veya Tarayın
        </div>

        <form onSubmit={submit} className="flex gap-2 mb-4">
          <input
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            placeholder="Barkod..."
            autoFocus
            data-testid="reprint-barcode-input"
            className="flex-1 px-3 py-3 border-2 border-slate-800 bg-white font-mono text-base focus:border-blue-700 focus:outline-none"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="submit"
            disabled={loading || !barcode.trim()}
            data-testid="reprint-submit-btn"
            className="px-5 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide text-sm disabled:opacity-50"
          >
            {loading ? "..." : "Bastır"}
          </button>
        </form>

        <div className="mb-3">
          <button
            onClick={() => setScannerOpen(v => !v)}
            data-testid="reprint-scanner-toggle"
            className={`inline-flex items-center gap-2 px-3 py-2 border-2 font-bold text-xs uppercase tracking-wide ${
              scannerOpen
                ? "border-slate-800 bg-slate-900 text-white"
                : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"
            }`}
          >
            <Camera size={14}/> {scannerOpen ? "Kamerayı Kapat" : "Kamerayla Tara"}
          </button>
        </div>

        {scannerOpen && (
          <div className="mb-3">
            <BarcodeCameraScanner autoStart={true} onDetected={(code) => { setBarcode(code); reprint(code); }} />
          </div>
        )}

        {lastResult && (
          <div className={`p-3 border-2 text-sm font-bold ${lastResult.ok ? "bg-emerald-50 border-emerald-600 text-emerald-900" : "bg-red-50 border-red-500 text-red-800"}`}>
            {lastResult.ok ? "✓ " : "✗ "}{lastResult.msg}
          </div>
        )}

        <div className="mt-4 p-3 bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed">
          <b>Nasıl çalışır?</b> Barkodu girip "Bastır" tuşuna basınca sistem 95×95mm PDF üretir ve otomatik olarak yazıcı diyaloğunu açar. PDF ayrıca dosya olarak da indirilir. Yazdırma diyaloğunda <b>"Ölçek: Gerçek boyut" (Actual size / %100)</b> seçin.
        </div>
      </div>
    </div>
  );
}

